import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { IonApp, setupIonicReact } from '@ionic/react';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ProveedorSesion } from './servicios/sesion.js';
import { configurarNativo } from './servicios/nativo.js';
import { ProveedorToasts, notificarErrorGlobal } from './componentes/notificaciones-globales.js';
import { ErrorApi } from './servicios/api.js';
import { App } from './app.js';

import '@ionic/react/css/core.css';
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

import './estilos/tokens.css';
import './estilos/app.css';
import { escucharRestauracionDesdeCache } from './servicios/cache-navegador.js';
import { aplicarTema, obtenerTemaPreferido } from './servicios/preferencias.js';

setupIonicReact();

// Antes del primer render para que no parpadee el tema equivocado.
aplicarTema(obtenerTemaPreferido());


const cliente = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // Un 401 lo resuelve el cliente renovando el token; reintentarlo aquí solo
      // multiplicaría las peticiones fallidas antes de mostrar el formulario.
      retry: (intentos, error) => intentos < 2 && !(error as { estado?: number }).estado,
    },
  },
  // Una auditoría encontró ~24 de 52 mutations en `paginas/*.tsx` sin ningún
  // `<Aviso>` que mostrara `mutation.error` — el motivo del backend (permiso
  // denegado, regla de negocio, validación) se perdía en silencio. Este
  // `onError` global es la red de seguridad: no reemplaza un aviso puntual
  // mejor ubicado, pero garantiza que ningún error quede sin mostrarse.
  mutationCache: new MutationCache({
    onError: (error) => {
      const mensaje = error instanceof ErrorApi ? error.message : 'Ocurrió un error inesperado.';
      notificarErrorGlobal(mensaje);
    },
  }),
});

void configurarNativo();

const raiz = document.getElementById('raiz');
if (!raiz) throw new Error('Falta el elemento raíz en index.html');

// Al volver desde el caché del navegador, los datos y la sesión se revalidan con el servidor.
escucharRestauracionDesdeCache(cliente);

createRoot(raiz).render(
  <StrictMode>
    <IonApp>
      <QueryClientProvider client={cliente}>
        <ProveedorToasts>
          <BrowserRouter>
            <ProveedorSesion>
              <App />
            </ProveedorSesion>
          </BrowserRouter>
        </ProveedorToasts>
      </QueryClientProvider>
    </IonApp>
  </StrictMode>,
);
